import { ComponentProps, MutableRefObject } from 'react'

import { F0Box, F0Text } from '@factorialco/f0-react'
import type { F0FormRef } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { TrainingRequestDetailFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { isAbsoluteURL } from 'modules/core/lib/isAbsoluteURL'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { useCompetencies } from 'modules/trainings/hooks/Trainings/useCompetencies'

import { buildHeaderDetails, DraftForm, RichTextSection } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailSidepanelComponent/shared'
import { useNewProposalForm } from './form'

function getScheduleLabel(schedule: string | null | undefined): string {
  switch (schedule) {
    case 'workinghours':
      return i18n.t('trainings.training_requests.create.schedule_options.during_work_hours')
    case 'mixedhours':
      return i18n.t('trainings.training_requests.create.schedule_options.mixed_working_hours')
    case 'notsure':
      return i18n.t('trainings.training_requests.create.schedule_options.not_sure')
    case 'outsideworkinghours':
      return i18n.t('trainings.training_requests.create.schedule_options.outside_working_hours')
    default:
      return schedule ?? '-'
  }
}

type Props = {
  trainingRequest: TrainingRequestDetailFragment
  id?: string
  isDraft?: boolean
  formRef?: MutableRefObject<F0FormRef | null>
  onSuccess?: () => void
  onFormReady?: (helpers: { setSendForApproval: (value: boolean) => void }) => void
}

export const NewProposal = ({
  trainingRequest,
  isDraft = false,
  formRef,
  onSuccess,
  onFormReady,
}: Props) => {
  const headerDetails = buildHeaderDetails(
    trainingRequest,
    i18n.t('trainings.training_requests.create.button.new'),
    { isDraft }
  )

  if (isDraft) {
    return (
      <NewProposalDraft
        trainingRequest={trainingRequest}
        headerDetails={headerDetails}
        formRef={formRef}
        onSuccess={onSuccess}
        onFormReady={onFormReady}
      />
    )
  }

  return (
    <NewProposalReadOnly
      trainingRequest={trainingRequest}
      headerDetails={headerDetails}
    />
  )
}

// --- Draft mode ---

const NewProposalDraft = ({
  trainingRequest,
  headerDetails,
  formRef,
  onSuccess,
  onFormReady,
}: {
  trainingRequest: TrainingRequestDetailFragment
  headerDetails: ComponentProps<typeof DetailsItemsList>['details']
  formRef?: MutableRefObject<F0FormRef | null>
  onSuccess?: () => void
  onFormReady?: (helpers: { setSendForApproval: (value: boolean) => void }) => void
}) => {
  const formHook = useNewProposalForm({ trainingRequest, onSuccess })

  return (
    <DraftForm
      formName="new-proposal-training-request"
      formHook={formHook}
      headerDetails={headerDetails}
      formRef={formRef}
      onFormReady={onFormReady}
    />
  )
}

// --- Read-only mode ---

const NewProposalReadOnly = ({
  trainingRequest,
  headerDetails,
}: {
  trainingRequest: TrainingRequestDetailFragment
  headerDetails: ComponentProps<typeof DetailsItemsList>['details']
}) => {
  const { company } = useCurrentSession()
  const { competencies } = useCompetencies()
  const basicInfoDetails = getBasicInfoDetails(trainingRequest, competencies)
  const timingsDetails = getTimingsDetails(trainingRequest, company.currency || '')

  return (
    <F0Box display="flex" flexDirection="column" gap="xl" paddingBottom="xl">
      <DetailsItemsList details={headerDetails} tableView />

      <RichTextSection
        label={i18n.t('trainings.training_requests.my_requests.detail.training_need')}
        content={trainingRequest.trainingNeed ?? ''}
      />

      <RichTextSection
        label={i18n.t('trainings.training_requests.my_requests.detail.additional_comments')}
        content={trainingRequest.comments ?? ''}
      />

      {basicInfoDetails.length > 0 && (
        <F0Box display="flex" flexDirection="column" gap="xl" paddingBottom="xl" paddingTop="xl">
          <F0Text
            variant="label"
            content={i18n.t('trainings.training_requests.my_requests.detail.basic_information')}
          />
          <DetailsItemsList details={basicInfoDetails} tableView />
        </F0Box>
      )}

      {timingsDetails.length > 0 && (
        <F0Box display="flex" flexDirection="column" gap="xl">
          <F0Text
            variant="label"
            content={i18n.t('trainings.training_requests.my_requests.detail.timings_and_costs')}
          />
          <DetailsItemsList details={timingsDetails} tableView />
        </F0Box>
      )}

      <F0Box display="flex" flexDirection="column" gap="xl">
        <ApprovalsInformationBlock
          resourceId={String(trainingRequest.id)}
          resourceType={'Trainings::TrainingRequest'}
          newLook
        />
      </F0Box>
    </F0Box>
  )
}

// --- Detail builders ---

type Competency = { id: string; name: string }

function getBasicInfoDetails(
  trainingRequest: TrainingRequestDetailFragment,
  competencies: Competency[] | undefined
): ComponentProps<typeof DetailsItemsList>['details'] {
  const details: ComponentProps<typeof DetailsItemsList>['details'] = []

  if (trainingRequest.providerName) {
    details.push({
      title: i18n.t('trainings.training_requests.my_requests.detail.provider_name'),
      content: { type: 'item', text: trainingRequest.providerName },
    })
  }

  if (trainingRequest.externalReference) {
    const externalRef = trainingRequest.externalReference
    details.push({
      title: i18n.t('trainings.training_requests.my_requests.detail.external_link'),
      content: {
        type: 'item',
        text: i18n.t('trainings.training_requests.my_requests.detail.link'),
        action: isAbsoluteURL(externalRef)
          ? { type: 'open-link', href: externalRef }
          : undefined,
      },
    })
  }

  const competencyIds = trainingRequest.competencyIds ?? []
  if (competencyIds.length > 0 && competencies) {
    const competencyNames = competencyIds
      .map((cId) => competencies.find((c) => c.id === cId)?.name)
      .filter(Boolean)

    if (competencyNames.length > 0) {
      details.push({
        title: i18n.t('trainings.training_requests.create.competencies_label'),
        content: competencyNames.map((name) => ({
          type: 'dot-tag' as const,
          text: name ?? '',
          color: 'smoke' as const,
        })),
      })
    }
  }

  return details
}

function getTimingsDetails(
  trainingRequest: TrainingRequestDetailFragment,
  currency: string
): ComponentProps<typeof DetailsItemsList>['details'] {
  const details: ComponentProps<typeof DetailsItemsList>['details'] = []

  if (trainingRequest.startDate) {
    details.push({
      title: i18n.t('trainings.training_requests.my_requests.detail.start_date'),
      content: {
        type: 'item',
        text: dateToString(moment(trainingRequest.startDate), 'ui'),
      },
    })
  }

  if (trainingRequest.endDate) {
    details.push({
      title: i18n.t('trainings.training_requests.my_requests.detail.end_date'),
      content: {
        type: 'item',
        text: dateToString(moment(trainingRequest.endDate), 'ui'),
      },
    })
  }

  if (trainingRequest.duration != null) {
    details.push({
      title: i18n.t('trainings.training_requests.my_requests.detail.total_training_hours'),
      content: { type: 'item', text: `${trainingRequest.duration}h` },
    })
  }

  if (trainingRequest.schedule) {
    details.push({
      title: i18n.t('trainings.training_requests.my_requests.detail.training_schedule'),
      content: { type: 'item', text: getScheduleLabel(trainingRequest.schedule) },
    })
  }

  if (trainingRequest.cost != null) {
    details.push({
      title: i18n.t('trainings.training_requests.my_requests.detail.cost'),
      content: { type: 'item', text: `${trainingRequest.cost} ${currency}` },
    })
  }

  details.push({
    title: i18n.t('trainings.training_requests.my_requests.detail.subsidized_cost'),
    content: {
      type: 'item',
      text: trainingRequest.subsidized ? i18n.t('common.yes') : i18n.t('common.no'),
    },
  })

  return details
}
